import RefreshIcon from "@mui/icons-material/Refresh";
import { Stack } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { CSVLink } from "react-csv";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { CustomContainer } from "../../../../components/Containers";
import GlobalButton from "../../../../components/CustomButton";
import {
  exportInventoryMovements,
  getInventoryMovements,
} from "../../../../services/supplier/productApiFn";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import moment from "moment";
import { INVENTORY_MOVEMENT_REASON } from "../../../../constants/Variables";
import { handleOpenSnackBar } from "../../../../utils";
import {
  setInventoryCurrentPage,
  setInventoryPageLimit,
  setInventoryTotalCount,
  setInventoryTotalPages,
} from "../../../../lib/slices/paginationSlice";
import useDebounce from "../../../../hooks/useDebounce";
import {
  setInventoryMovementEndDate,
  setInventoryMovementSearchValue,
  setInventoryMovementStartDate,
  setInventoryMovementType,
} from "../../../../lib/slices/search_key_slice";
import DynamicPaginatedTable from "../../../../components/Tables/DynamicPaginatedTable";
import { PricingDialog } from "./PricingDialog";
import { Filters } from "./Filters";

const headerData = [
  "createdAt",
  "productDetails",
  "common.reason",
  "common.quantityChange",
  "common.previousQuantity",
  "common.currentQuantity",
  "common.pricingDetails",
];

const tableRows = [
  "created_date",
  "inventory_product_detail",
  "inventory_movement_reason",
  "inventory_quantity_change",
  "inventory_previous_quantity",
  "inventory_current_quantity",
  "inventory_pricing_details",
];

export function Inventory({ handleChangeTab }) {
  // Dispatch
  const dispatch = useDispatch();
  // Translation Hook
  const { t } = useTranslation();
  // CSV Link Ref
  const csvLinkRef = useRef(null);
  // Pricing Dialog Ref
  const pricingDialogRef = useRef(null);
  // loading state
  const [isLoading, setIsLoading] = useState({
    allInventory: true,
    exportInventory: false,
  });
  // inventory state
  const [allInventory, setAllInventory] = useState([]);
  const [exportData, setExportData] = useState([]);
  const [shouldDownloadCsv, setShouldDownloadCsv] = useState(false);
  // Get Current Language from Redux
  const { currentLanguage } = useSelector((state) => state.translationReducer);
  // Get User Data from Redux
  const userData = useSelector((state) => state.userData.user);
  // Get Pagination from Redux
  const pagination = useSelector((state) => state.paginationReducer);
  // Get Search Keys from Redux
  const searchKeys = useSelector((state) => state.SearchKeysReducer.searchKeys);
  // Get Debounced Search Term
  const debouncedSearchTerm = useDebounce(
    searchKeys.inventoryMovementSearchValue,
    1000,
    true,
    () => {
      dispatch(setInventoryCurrentPage(1));
    },
  );

  // fetch inventory movements
  const fetchInventoryMovements = async (
    search,
    movementType,
    page,
    limit,
    startDate,
    endDate,
  ) => {
    try {
      const params = {
        supplierId: userData?._id,
        search: search,
        movementType: movementType,
        page: page,
        limit: limit,
        startDate: startDate,
        endDate: endDate,
      };
      setIsLoading((prev) => ({ ...prev, allInventory: true }));
      const response = await getInventoryMovements(params);
      if (response.data.status === "success") {
        const pagination = response?.data.data.pagination;
        let data = response?.data.data.movements;
        setAllInventory(data);
        dispatch(setInventoryPageLimit(pagination.limit));
        dispatch(setInventoryCurrentPage(pagination.page));
        dispatch(setInventoryTotalCount(pagination.total));
        dispatch(setInventoryTotalPages(pagination.totalPages));
      } else {
        handleOpenSnackBar("error", response.data.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
    } finally {
      setIsLoading((prev) => ({ ...prev, allInventory: false }));
    }
  };

  useEffect(() => {
    if (userData) {
      fetchInventoryMovements(
        debouncedSearchTerm,
        searchKeys.inventoryMovementType,
        pagination.inventoryPage,
        pagination.inventoryLimit,
        searchKeys.inventoryMovementStartDate,
        searchKeys.inventoryMovementEndDate,
      );
    }
  }, [
    userData,
    debouncedSearchTerm,
    searchKeys.inventoryMovementType,
    pagination.inventoryPage,
    pagination.inventoryLimit,
  ]);

  // Prepare Export Data
  const prepareExportData = async () => {
    try {
      const query = {
        supplierId: userData?._id,
      };
      const response = await exportInventoryMovements(query);
      setIsLoading((prev) => ({ ...prev, exportInventory: true }));
      if (response.data.status === "success") {
        let data = response?.data.data;
        return data.map((item) => ({
          "Created At": moment(item.createdAt).format("MM-DD-YYYY") || "-",
          "Updated At": moment(item.updatedAt).format("MM-DD-YYYY") || "-",
          "Product Name": item.productId?.name[currentLanguage] || "-",
          "Product SKU": item.sku || "-",
          Reason:
            INVENTORY_MOVEMENT_REASON.find((x) => x.value === item.reason)
              ?.label[currentLanguage] || "-",
          "Quantity Change": item.quantityChange,
          "Previous Quantity": item.previousQuantity,
          "Current Quantity": item.currentQuantity,
        }));
      } else {
        handleOpenSnackBar("error", response.data.message);
        return [];
      }
    } catch (error) {
      handleOpenSnackBar("error", error.message);
      return [];
    } finally {
      setIsLoading((prev) => ({ ...prev, exportInventory: false }));
    }
    return [];
  };

  // Handle Download CSV
  useEffect(() => {
    if (shouldDownloadCsv && exportData.length > 0) {
      csvLinkRef.current?.link?.click();
      setShouldDownloadCsv(false);
    }
  }, [shouldDownloadCsv, exportData]);

  // Handle Export CSV
  const handleExportCsv = async () => {
    const data = await prepareExportData();
    if (data.length === 0) return;
    setExportData(data);
    setShouldDownloadCsv(true);
  };

  // handle page change
  const handlePageChange = (event, newPage) => {
    const page = newPage + 1;
    dispatch(setInventoryCurrentPage(page));
  };

  // handle view pricing details
  const handleViewPricingDetails = (row) => {
    console.log(row, "rowrowrowrow");
    pricingDialogRef.current.openDialog({ data: row });
  };

  // handle rows per page change
  const handleRowsPerPageChange = (event) => {
    const limit = parseInt(event.target.value, 10);
    dispatch(setInventoryPageLimit(limit));
    dispatch(setInventoryCurrentPage(1));
  };

  // handle reset filters
  const handleResetFilters = () => {
    dispatch(setInventoryMovementSearchValue(""));
    dispatch(setInventoryMovementType("All"));
    dispatch(setInventoryMovementStartDate(null));
    dispatch(setInventoryMovementEndDate(null));
    dispatch(setInventoryCurrentPage(1));
    fetchInventoryMovements(
      "",
      "All",
      1,
      pagination.inventoryLimit,
      null,
      null,
    );
  };

  // Render Header Actions
  const renderHeaderActions = () => {
    return (
      <Stack
        gap={1}
        flexGrow={1}
        direction={"row"}
        alignItems={"center"}
        justifyContent={"flex-end"}
      >
        <GlobalButton
          variant={"export_button"}
          btnText={""}
          startIcon={<RefreshIcon color="primary" />}
          handleClickBtn={handleResetFilters}
          styleProps={{
            width: 40,
            minWidth: 40,
            height: 40,
            p: 0,
            "& .MuiButton-startIcon": {
              margin: 0,
            },
          }}
          disabled={isLoading.allInventory}
        />
        <CSVLink
          ref={csvLinkRef}
          data={exportData}
          filename={"inventory-movements.csv"}
          className="csv-link"
          style={{ display: "none" }}
        />
        <GlobalButton
          variant={"export_button"}
          btnText={"export"}
          startIcon={<FileDownloadIcon color="primary" />}
          handleClickBtn={handleExportCsv}
          styleProps={{
            width: "auto",
            height: 40,
          }}
          disabled={isLoading.exportInventory}
        />
      </Stack>
    );
  };

  // render table
  const renderTable = useMemo(() => {
    return (
      <DynamicPaginatedTable
        tableWidth={1200}
        tableHeader={headerData}
        displayRows={tableRows}
        tableData={allInventory || []}
        isLoading={isLoading.allInventory}
        showDynamicPagination={true}
        paginatedData={{
          page: pagination.inventoryPage,
          limit: pagination.inventoryLimit,
          total: pagination.inventoryTotal,
          totalPages: pagination.inventoryTotalPages,
        }}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        handleViewPricingDetails={handleViewPricingDetails}
      />
    );
  }, [allInventory, isLoading.allInventory]);

  return (
    <CustomContainer
      primaryHeading={t("supplierProductTab.inventoryMovements")}
      secondaryHeading={t("supplierProductTab.viewInventoryMovements")}
      customHeaderActions={renderHeaderActions()}
      filterSection={<Filters fetchData={fetchInventoryMovements} />}
    >
      {/* Render Table */}
      {renderTable}

      {/* Render Pricing Dialog */}
      <PricingDialog ref={pricingDialogRef} />
    </CustomContainer>
  );
}
